import { accessTokenCookieName, backendBaseUrl } from '@/lib/constants';
import axios from 'axios';
import cookies from 'js-cookie';
import { toast } from 'sonner';
import { handleLogout, refreshAccessToken } from './auth.js';
import { parseBlobToJson } from '@/lib/utils.js';
import { sentryUtils } from '@/utils/sentry.js';

export const Axios = axios.create({
  baseURL: backendBaseUrl,
});

const getRequestConfig = (config) => {
  const token = cookies.get(accessTokenCookieName);
  if (token) {
    config.headers.Authorization = token;
    config.headers['ngrok-skip-browser-warning'] = 1;
  }
  return config;
};
const getRequestError = (error) => {
  console.log('Request Error', error);
  sentryUtils.trackApiError(error, 'request', 'REQUEST');
  return Promise.reject(error);
};
const getResponseError = async (error) => {
  // Track API errors with Sentry
  const endpoint = error.config?.url || 'unknown';
  const method = error.config?.method || 'unknown';
  sentryUtils.trackApiError(error, endpoint, method);

  if (error.response) {
    // Request made and server responded
    switch (error.response.status) {
      case 400: {
        console.log('Bad Request');
        break;
      }
      case 401: {
        const originalRequest = error.config;
        // Prevent infinite loop: don't retry if the request is to the refresh endpoint
        if (
          originalRequest.url &&
          originalRequest.url.includes('/auth/refresh-token')
        ) {
          handleLogout();
          break;
        }
        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return Axios(originalRequest);
          }
        } catch (refreshError) {
          console.log('Refresh token failed:', refreshError);
          sentryUtils.captureException(refreshError, {
            context: 'token_refresh',
            endpoint,
            method,
          });
          handleLogout();
        }

        break;
      }

      case 403: {
        console.log('Forbidden');
        break;
      }
      case 404: {
        console.log('Not Found');
        break;
      }
      case 500: {
        console.log('Internal Server Error');
        sentryUtils.captureMessage('Server Error (500)', 'error', {
          endpoint,
          method,
          status: 500,
        });
        break;
      }
      case 502: {
        console.log('Bad Gateway');
        break;
      }
      case 503: {
        console.log('Service Unavailable');
        break;
      }
      case 504: {
        console.log('Gateway Timeout');
        break;
      }
      default: {
        break;
      }
    }
  } else if (error.request) {
    // The request was made but no response was received
    console.log('Error Request :', error.request);
    sentryUtils.captureMessage('Network Error - No Response', 'warning', {
      endpoint,
      method,
    });
  } else {
    // Something happened in setting up the request that triggered an Error
    console.log('Error Message :', error.message);
  }
  if (
    Array.isArray(error?.response?.data?.errors) &&
    error?.response?.data?.errors?.length > 0
  ) {
    error.response.data.errors?.forEach((message) => {
      toast.error(message ? `errors.${message}` : 'Something went wrong!');
    });
  } else if (error.response?.data instanceof Blob) {
    const errorData = await parseBlobToJson(error.response.data);
    toast.error(errorData.message || error.message);
  } else if (error?.response?.data?.message) {
    if (error.response.data.message !== 'Consent Not Found') {
      toast.error(error.response.data.message || 'Something went wrong!');
    }
  } else {
    toast.error(
      error?.message ? `errors.${error?.message}` : 'Something went wrong!',
    );
  }

  return Promise.reject(error);
};
const getResponseConfig = (response) => {
  return response.data;
};
Axios.interceptors.request.use(getRequestConfig, getRequestError);
Axios.interceptors.response.use(getResponseConfig, getResponseError);
